import DataForm from 'interfaces/PersonalDataForm';
import { validateBSN } from 'elfproef';
import Labels from '../enums/Labels';
import { logFormValidationError } from './utils';
import { isBefore, isValid } from 'date-fns';


const MinAge = 18;
const MaxAge = 75; // max 85 - min age(18)

interface ValidationRules {
  [key: string]: string;
}

interface Values {
  [key: string]: string | undefined | null | boolean | Date;
}

interface Errors {
  [key: string]: Labels;
}

export const emailRegexp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const zipCodeRegexp = /\d{4}\s?[A-Z]{2}/;

export function checkIsDefined(input: string | undefined | null): input is string {
  return !(input === undefined || input === null || input === '');
}

export function checkEmail(email: string): boolean {
  return emailRegexp.test(String(email).toLowerCase());
}

function checkLocalZipCode(zipCode: string): boolean {
  return zipCodeRegexp.test(zipCode);
}

export function isRequired(fieldName: string, errorObject: Errors, values: Values): void {
  const value = values[fieldName] as string | undefined | null;
  if (!checkIsDefined(value)) {
    errorObject[fieldName] = Labels.ERROR_REQUIRED;
    logFormValidationError({
      fieldName,
      error: Labels.ERROR_REQUIRED,
    });
  }
}

export function isEmail(fieldName: string, errorObject: Errors, values: Values): void {
  const value = values[fieldName] as string;
  if (!checkEmail(value)) {
    errorObject[fieldName] = Labels.ERROR_INVALID_EMAIL;
    logFormValidationError({
      fieldName,
      error: Labels.ERROR_INVALID_EMAIL,
    });
  }
}

export function isLocalZipCode(fieldName: string, errorObject: Errors, values: Values): void {
  const value = values[fieldName] as string;
  if (!checkLocalZipCode(value)) {
    errorObject[fieldName] = Labels.ERROR_INVALID_ZIP_CODE;
    logFormValidationError({
      fieldName,
      error: Labels.ERROR_INVALID_ZIP_CODE,
    });
  }
}

const phoneValidationRules: ValidationRules = {
  '+31': '[+]316[0-9]{8}',
  '+32': '[+]324[0-9]{8}',
  '+49': '[+]491[5-7]{1}[0-9]{8,9}',
};

function checkRegExpRule(formValue: string, rule: string): boolean {
  const regExp = new RegExp(rule);
  return regExp.test(formValue);
}

export function isNumberNumeric(fieldName: string, errorObject: Errors, values: Values): void {
  const value = values[fieldName] as string;
  const validationRule = '^[0-9]+$';
  if (!checkRegExpRule(value, validationRule)) {
    errorObject[fieldName] = Labels.ERROR_HOUSE_NUMBER_IS_NOT_NUMERIC;
    logFormValidationError({
      fieldName,
      error: Labels.ERROR_HOUSE_NUMBER_IS_NOT_NUMERIC,
    });
  }
}

export function isValidPhone(
  fieldNamePhonePrefix: string,
  fieldNamePhoneBody: string,
  errorObject: Errors,
  values: Values,
): void {
  const prefixValue = values[fieldNamePhonePrefix] as string;
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const value = `${values[fieldNamePhonePrefix]}${values[fieldNamePhoneBody]}`;
  const validationRule = phoneValidationRules[prefixValue];
  if (!checkRegExpRule(value, validationRule)) {
    errorObject[fieldNamePhoneBody] = Labels.ERROR_INVALID_PHONE;
    logFormValidationError({
      fieldName: `${fieldNamePhonePrefix}-${fieldNamePhoneBody}`,
      error: Labels.ERROR_INVALID_PHONE,
    });
  }
}

function isValidGermanSSN(ssn: string) {
  if (ssn.length !== 12) {
    return false;
  }

  return true;
}

function isValidOldGermanSSN(ssn: string) {
  if (ssn.length !== 10) {
    return false;
  }

  if (!/^\d+$/.test(ssn)) {
    return false;
  }

  return true;
}

export function isValidSSN(
  fieldName: keyof DataForm,
  errorObject: Errors,
  values: Values,
): void {
  const rawSsn = values[fieldName] as string;
  const ssn = rawSsn.replace(/\s+/g, '');

  if (!validateBSN(ssn) && !isValidOldGermanSSN(ssn) && !isValidGermanSSN(ssn)) {
    errorObject[fieldName] = Labels.ERROR_INVALID_SSN;
    logFormValidationError({
      fieldName,
      error: Labels.ERROR_INVALID_SSN,
    });
  }
}
export function isValidDate(value: string | undefined): boolean {
  if (!value) return false;
  const date = new Date(value);
  const dateMin = new Date();
  const dateMax = new Date();
  dateMin.setFullYear(dateMin.getFullYear() - MinAge);
  dateMax.setFullYear(dateMax.getFullYear() + MaxAge);
  return isValid(date) && isBefore(date, dateMin) && isBefore(date, dateMax);
}

export function validateDate(fieldName: string, errorObject: Errors, values: Values): void {
  const value = values[fieldName] as string;
  if (!isValidDate(value)) {
    errorObject[fieldName] = Labels.ERROR_INVALID_DATE;
    logFormValidationError({
      fieldName,
      error: Labels.ERROR_INVALID_DATE,
    });
  }
}
